/* VAST Overview scroll motion. Sections render their static end frames without this script;
   every controller restores that markup when destroyed (reduced motion, breakpoint change, theme editor). */
(() => {
  if (window.VastMotion) return;

  const mqReduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const mqMobile = window.matchMedia('(max-width: 749px)');
  const clamp = (v, a = 0, b = 1) => Math.min(b, Math.max(a, v));
  const smooth = (v) => {
    const t = clamp(v);
    return t * t * (3 - 2 * t);
  };
  const cssPx = (el, prop) => parseFloat(getComputedStyle(el).getPropertyValue(prop)) || 0;

  const controllers = new Map();
  let raf = 0;
  const schedule = () => {
    if (!raf) raf = requestAnimationFrame(tick);
  };
  function tick(now) {
    raf = 0;
    let again = false;
    controllers.forEach((c) => {
      if (c.update(now)) again = true;
    });
    if (again) schedule();
  }

  function pinScreens(section, screens, dist) {
    const pin = document.createElement('div');
    pin.className = 'vast-ov-pin';
    screens[0].before(pin);
    screens.forEach((s) => pin.append(s));
    section.style.setProperty('--vo-dist', `${dist}px`);
    section.classList.add('is-pinned');
    return {
      pin,
      progress() {
        return clamp((cssPx(section, '--nav') - section.getBoundingClientRect().top) / dist);
      },
      near() {
        const r = section.getBoundingClientRect();
        return r.bottom > -window.innerHeight && r.top < window.innerHeight * 2;
      },
      destroy() {
        screens.forEach((s) => pin.before(s));
        pin.remove();
        section.classList.remove('is-pinned');
        section.style.removeProperty('--vo-dist');
      },
    };
  }

  /* Sequence: 198-frame canvas scrub, scenes start at frames 0 / 52 / 140, 20% hold at the end. */
  function sequence(section) {
    const screens = [...section.querySelectorAll('.vast-ov-sq__screen')];
    const first = section.dataset.frameFirst || '';
    const count = parseInt(section.dataset.frameCount, 10) || 0;
    if (screens.length !== 3 || !first || count < 3) return null;

    const mobile = mqMobile.matches;
    const last = count - 1;
    const scenes = [0, 52, 140].map((f) => Math.round((f * last) / 197));
    const ext = first.split('.').pop();
    const base = first.replace(/\d{3}\.[a-z0-9]+$/i, '');
    const dpr = Math.min(window.devicePixelRatio || 1, mobile ? 1.5 : 2);
    const width = mobile ? 1200 : dpr > 1 ? 0 : 1600;
    const src = (i) => `${base}${String(i).padStart(3, '0')}.${ext}${width ? `?width=${width}` : ''}`;
    const p = pinScreens(section, screens, (mobile ? 4200 : 5200) * 1.2);

    const canvas = document.createElement('canvas');
    canvas.className = 'vast-ov-sq__canvas';
    canvas.setAttribute('aria-hidden', 'true');
    p.pin.append(canvas);
    const ctx = canvas.getContext('2d');
    const bgs = screens.map((s) => s.querySelector('.vast-ov-sq__bg'));
    const keep = new Set([...scenes, last]);
    const cache = new Map();
    const loading = new Map();
    let queue = [];
    let cur = 0;
    let frame = -1;
    let dir = 1;
    let drawn = -1;
    let scene = -1;
    let lastNow = 0;
    let w = 0;
    let h = 0;
    let dead = false;

    const sceneOf = (f) => (f >= scenes[2] ? 2 : f >= scenes[1] ? 1 : 0);

    function resize() {
      w = p.pin.clientWidth;
      h = p.pin.clientHeight;
      canvas.width = Math.max(1, Math.round(w * dpr));
      canvas.height = Math.max(1, Math.round(h * dpr));
      drawn = -1;
    }

    function pump() {
      while (!dead && loading.size < (mobile ? 3 : 4) && queue.length) {
        const i = queue.shift();
        if (cache.has(i) || loading.has(i)) continue;
        const img = new Image();
        img.decoding = 'async';
        loading.set(i, img);
        img.onload = () => {
          loading.delete(i);
          if (dead) return;
          cache.set(i, img);
          evict();
          drawn = -1;
          schedule();
          pump();
        };
        img.onerror = () => {
          loading.delete(i);
          pump();
        };
        img.src = src(i);
      }
    }

    function want(f) {
      const list = [f, scenes[sceneOf(f)], ...keep];
      for (let k = 1; k <= 16; k += 1) {
        list.push(f + k * dir);
        if (k <= 6) list.push(f - k * dir);
      }
      queue = [...new Set(list)].filter((i) => i >= 0 && i <= last && !cache.has(i) && !loading.has(i));
      pump();
    }

    function evict() {
      const max = mobile ? 28 : 40;
      if (cache.size <= max) return;
      const order = [...cache.keys()]
        .filter((i) => !keep.has(i) && i !== drawn)
        .sort((a, b) => Math.abs(b - frame) - Math.abs(a - frame));
      while (cache.size > max && order.length) cache.delete(order.shift());
    }

    function nearest(f) {
      if (cache.has(f)) return f;
      const s = sceneOf(f);
      let best = -1;
      let d = Infinity;
      cache.forEach((_, i) => {
        if (sceneOf(i) !== s) return;
        const dd = Math.abs(i - f);
        if (dd < d) {
          d = dd;
          best = i;
        }
      });
      return best;
    }

    function draw(f) {
      const i = nearest(f);
      if (i < 0 || i === drawn || !w) return;
      const img = cache.get(i);
      const scale = Math.max(w / img.naturalWidth, h / img.naturalHeight) * (mobile ? 0.75 : 1);
      const dw = img.naturalWidth * scale;
      const dh = img.naturalHeight * scale;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, w, h);
      ctx.drawImage(img, (w - dw) / 2, (h - dh) * (mobile ? 0.1 : 0.5), dw, dh);
      drawn = i;
      section.classList.add('has-canvas');
    }

    function setScene(s) {
      if (s === scene) return;
      scene = s;
      section.dataset.scene = String(s + 1);
      screens.forEach((el, k) => el.classList.toggle('is-active', k === s));
    }

    resize();
    setScene(0);

    return {
      resize,
      update(now) {
        if (!p.near()) {
          lastNow = 0;
          return false;
        }
        const target = Math.min(1, p.progress() * 1.2) * last;
        const dt = lastNow ? Math.min(64, now - lastNow) : 16;
        lastNow = now;
        cur += (target - cur) * (1 - Math.exp(-dt / 120));
        if (Math.abs(target - cur) < 0.02) cur = target;
        const f = clamp(Math.floor(cur + 1e-5), 0, last);
        if (f !== frame) {
          dir = f >= frame ? 1 : -1;
          frame = f;
          want(f);
        }
        draw(f);
        const be = mobile ? 9 : 12;
        bgs.forEach((bg, k) => {
          if (k && bg) bg.style.opacity = String(smooth((cur - (scenes[k] - be)) / (be * 2)));
        });
        setScene(sceneOf(f));
        if (cur !== target) return true;
        lastNow = 0;
        return false;
      },
      destroy() {
        dead = true;
        loading.forEach((img) => {
          img.onload = null;
          img.onerror = null;
          img.src = '';
        });
        cache.clear();
        canvas.remove();
        bgs.forEach((bg) => bg && bg.style.removeProperty('opacity'));
        screens.forEach((el) => el.classList.remove('is-active'));
        section.classList.remove('has-canvas');
        delete section.dataset.scene;
        p.destroy();
      },
    };
  }

  /* Mood: pinned crossfade, holds at 0-.2 / .4-.6 / .8-1. */
  function mood(section) {
    const screens = [...section.querySelectorAll('.vast-ov-md__screen')];
    if (screens.length < 2) return null;
    const p = pinScreens(section, screens, 2400);
    const span = 1 / (screens.length * 2 - 1);
    return {
      update() {
        const t = p.progress();
        screens.forEach((el, k) => {
          if (!k) return;
          const a = smooth((t - span * (2 * k - 1)) / span);
          el.style.opacity = String(a);
          el.style.visibility = a > 0 ? '' : 'hidden';
        });
        return false;
      },
      destroy() {
        screens.forEach((el) => {
          el.style.removeProperty('opacity');
          el.style.removeProperty('visibility');
        });
        p.destroy();
      },
    };
  }

  /* Tri-core mobile: pinned horizontal carousel of the three modes. */
  function triCoreMobile(section) {
    const screens = [...section.querySelectorAll('.vast-ov-tc__screen')];
    const modes = screens.map((s) => s.querySelector('.vast-ov-tc__mode'));
    if (screens.length !== 3 || modes.some((m) => !m)) return null;
    const p = pinScreens(section, screens, 2000);
    return {
      update() {
        const t = p.progress() * 4;
        const pos = smooth((t - 0.8) / 0.6) + smooth((t - 2.4) / 0.6);
        const width = p.pin.clientWidth;
        modes.forEach((m, k) => {
          m.style.transform = `translate3d(${((k - pos) * width).toFixed(2)}px, 0, 0)`;
        });
        return false;
      },
      destroy() {
        modes.forEach((m) => m.style.removeProperty('transform'));
        p.destroy();
      },
    };
  }

  /* Tri-core desktop: one-time staggered reveal. */
  function triCoreDesktop(section) {
    if (section.getBoundingClientRect().top < window.innerHeight * 0.75) return null;
    section.classList.add('is-reveal');
    let done = false;
    let timer = 0;
    return {
      update() {
        if (done || section.getBoundingClientRect().top > window.innerHeight * 0.7) return false;
        done = true;
        section.classList.add('is-in');
        timer = setTimeout(() => section.classList.remove('is-reveal', 'is-in'), 2800);
        return false;
      },
      destroy() {
        clearTimeout(timer);
        section.classList.remove('is-reveal', 'is-in');
      },
    };
  }

  /* Leak: desktop sides spread from the centre, tech fades up, then callout lines draw. */
  function leak(section) {
    if (section.getBoundingClientRect().top < window.innerHeight * 0.6) return null;
    const mobile = mqMobile.matches;
    const svg = section.querySelector(mobile ? '.vast-ov-lk__notes--mb' : '.vast-ov-lk__notes--dt');
    const trip = section.querySelector('.vast-ov-lk__side--trip');
    const flight = section.querySelector('.vast-ov-lk__side--flight');
    const tech = section.querySelector('.vast-ov-lk__tech');
    if (!svg || !trip || !flight || !tech) return null;
    const lines = [...svg.querySelectorAll('[data-leak-line]')];
    let drawn = false;
    let timer = 0;

    function arm() {
      clearTimeout(timer);
      const scale = Math.max(1, svg.getBoundingClientRect().width / 472);
      lines.forEach((l) => {
        const len = Math.ceil(l.getTotalLength() * scale) + 2;
        l.style.transition = 'none';
        l.style.strokeDasharray = String(len);
        l.style.strokeDashoffset = String(len);
      });
      section.classList.remove('is-drawn');
      drawn = false;
    }

    function drawLines() {
      lines.forEach((l, k) => {
        l.style.transition = `stroke-dashoffset .55s linear ${k * 0.06}s`;
        l.style.strokeDashoffset = '0';
      });
      section.classList.add('is-drawn');
      drawn = true;
      timer = setTimeout(() => {
        lines.forEach((l) => {
          l.style.removeProperty('transition');
          l.style.removeProperty('stroke-dasharray');
          l.style.removeProperty('stroke-dashoffset');
        });
      }, 1200);
    }

    section.classList.add('is-motion');
    arm();

    return {
      update() {
        const r = section.getBoundingClientRect();
        const vh = window.innerHeight;
        if (r.top > vh) {
          if (drawn) arm();
          if (!mobile) {
            trip.style.transform = '';
            flight.style.transform = '';
          }
          return false;
        }
        if (mobile) {
          if (!drawn && tech.getBoundingClientRect().top < vh * 0.6) drawLines();
          return false;
        }
        const t = clamp((vh - r.top) / Math.max(1, vh - cssPx(section, '--nav')));
        const gap = tech.offsetLeft - trip.offsetLeft - trip.offsetWidth;
        const k = (tech.offsetWidth + gap) / 2;
        const spread = 1 - smooth((t - 0.25) / 0.6);
        trip.style.transform = spread ? `translate3d(${(k * spread).toFixed(2)}px, 0, 0)` : '';
        flight.style.transform = spread ? `translate3d(${(-k * spread).toFixed(2)}px, 0, 0)` : '';
        const a = smooth((t - 0.6) / 0.35);
        tech.style.opacity = a < 1 ? String(a) : '';
        tech.style.transform = a < 1 ? `translate3d(0, ${(15 * (1 - a)).toFixed(2)}%, 0)` : '';
        if (!drawn && t >= 0.95) drawLines();
        return false;
      },
      destroy() {
        clearTimeout(timer);
        [trip, flight, tech].forEach((el) => {
          el.style.removeProperty('transform');
          el.style.removeProperty('opacity');
        });
        lines.forEach((l) => {
          l.style.removeProperty('transition');
          l.style.removeProperty('stroke-dasharray');
          l.style.removeProperty('stroke-dashoffset');
        });
        section.classList.remove('is-motion', 'is-drawn');
      },
    };
  }

  /* CoreGuard: 1s timeline once in view — night photo fades in, chart wipes and curves draw. */
  function coreGuard(section) {
    const mobile = mqMobile.matches;
    const scene = section.querySelector('.vast-ov-cg__scene');
    const night = section.querySelector('.vast-ov-cg__photo--night');
    const reveal = section.querySelector('[data-chart-reveal]');
    const paths = [...section.querySelectorAll('[data-chart-draw]')];
    if (!scene || !night || !reveal) return null;
    const trigger = () => window.innerHeight * (mobile ? 0.2 : 0.35);
    const anchor = mobile ? scene : section;
    if (anchor.getBoundingClientRect().top < trigger() + window.innerHeight * 0.25) return null;
    const full = parseFloat(reveal.getAttribute('width')) || 0;
    const lens = paths.map((pth) => Math.ceil(pth.getTotalLength()) + 2);
    let state = 'idle';
    let start = 0;

    function set(t) {
      night.style.opacity = String(smooth((t - 0.18) / 0.46));
      reveal.setAttribute('width', (full * clamp((t - 0.1) / 0.7)).toFixed(2));
      paths.forEach((pth, k) => {
        pth.style.strokeDasharray = String(lens[k]);
        pth.style.strokeDashoffset = String(lens[k] * (1 - clamp((t - 0.13) / 0.7)));
      });
    }
    function clear() {
      night.style.removeProperty('opacity');
      reveal.setAttribute('width', String(full));
      paths.forEach((pth) => {
        pth.style.removeProperty('stroke-dasharray');
        pth.style.removeProperty('stroke-dashoffset');
      });
    }

    set(0);

    return {
      update(now) {
        const top = anchor.getBoundingClientRect().top;
        if (state === 'idle' && top <= trigger()) {
          state = 'play';
          start = now;
        }
        if (state === 'play') {
          const t = (now - start) / 1000;
          if (t >= 1) {
            clear();
            state = 'done';
            return false;
          }
          set(t);
          return true;
        }
        if (state === 'done' && section.getBoundingClientRect().top > window.innerHeight) {
          set(0);
          state = 'idle';
        }
        return false;
      },
      destroy() {
        clear();
      },
    };
  }

  const types = [
    ['[data-vast-sequence]', sequence],
    ['[data-vast-mood]', mood],
    ['[data-vast-tri-core]', (s) => (mqMobile.matches ? triCoreMobile(s) : triCoreDesktop(s))],
    ['[data-vast-leak]', leak],
    ['[data-vast-core-guard]', coreGuard],
  ];

  function mount(root = document) {
    if (mqReduce.matches) return;
    types.forEach(([selector, create]) => {
      root.querySelectorAll(selector).forEach((section) => {
        if (controllers.has(section)) return;
        const c = create(section);
        if (c) controllers.set(section, c);
      });
    });
    schedule();
  }

  function unmount(root = document) {
    controllers.forEach((c, section) => {
      if (!root.contains(section)) return;
      c.destroy();
      controllers.delete(section);
    });
  }

  function remount() {
    unmount();
    mount();
  }

  mqMobile.addEventListener('change', remount);
  mqReduce.addEventListener('change', remount);
  window.addEventListener('scroll', schedule, { passive: true });
  window.addEventListener('resize', () => {
    controllers.forEach((c) => c.resize && c.resize());
    schedule();
  });
  document.addEventListener('shopify:section:load', (e) => mount(e.target));
  document.addEventListener('shopify:section:unload', (e) => unmount(e.target));

  window.VastMotion = { mount, unmount };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => mount());
  else mount();
})();
